"use client"

import { useEffect, useMemo, useState } from "react"
import { getCountdownUntil, type CampaignCountdown } from "./time"
import styles from "./cart-campaign-countdown.module.css"

const pad = (value: number) => String(value).padStart(2, "0")

const deadlineLabel = (endsAt: string) => {
  const date = new Date(endsAt)
  if (Number.isNaN(date.getTime())) return ""
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

export default function CartCampaignCountdown({
  campaign,
}: {
  campaign: { label: string; endsAt: string }
}) {
  const [remaining, setRemaining] = useState<CampaignCountdown | null>(null)
  const deadline = useMemo(() => deadlineLabel(campaign.endsAt), [campaign.endsAt])

  useEffect(() => {
    const update = () => setRemaining(getCountdownUntil(campaign.endsAt, new Date()))
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [campaign.endsAt])

  if (remaining?.expired) return null

  return (
    <section
      className={styles.root}
      data-testid="cart-campaign-countdown"
      aria-label={deadline ? `${campaign.label}, ${deadline} tarihinde sona eriyor.` : campaign.label}
    >
      <span className={styles.label}>{campaign.label}</span>
      <span className={styles.deadline} aria-hidden="true">
        {deadline ? `${deadline} son` : "Kampanya"}
      </span>
      <strong
        className={styles.timer}
        data-testid="cart-campaign-countdown-digits"
        aria-hidden="true"
      >
        {remaining ? (
          <>
            {remaining.days > 0 ? `${remaining.days}g ` : ""}
            {pad(remaining.hours)}:{pad(remaining.minutes)}:{pad(remaining.seconds)}
          </>
        ) : "--:--:--"}
      </strong>
    </section>
  )
}
